import React, { useState } from 'react';
import Axios from 'axios';
import styles from '@/components/shared/Correlations/Correlations.module.scss';
import * as Routes from '@/modules/routes';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import Text from '@/components/ui/Text';
import useLessonTagValues from '../hooks/useLessonTagValues';
import { TagType, LessonType } from '../types';
import { outlineStyles } from '@/components/shared/Correlations/TagCard/shared';

type ClearCacheResponse = {
  data: any
};

type LessonTagContentType = {
  isExpanded: boolean;
  programId: number;
  tag: TagType;
  lesson: LessonType;
  headerLabel: string;
  isSysadmin: boolean;
  toggleExpanded: (key: string) => void;
};

const LessonTagContent = ({
  isExpanded,
  programId,
  tag,
  lesson,
  headerLabel,
  isSysadmin,
  toggleExpanded,
}: LessonTagContentType) => {
  const [isClearingCache, setIsClearingCache] = useState(false);

  const { data: tagValues, isLoading } = useLessonTagValues({
    isEnabled: isExpanded, lessonId: lesson.id, programId, tagId: tag.id,
  });

  const handleClearingCache = (e: React.MouseEvent<HTMLButtonElement>, lessonId: number) => {
    e.preventDefault();
    setIsClearingCache(true);

    Axios.post<ClearCacheResponse, any>(Routes.plato_api_clear_lesson_cache_tags_path(),
      { lesson_id: lessonId, program_id: programId, tag_id: tag.id })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        setIsClearingCache(false);
      }).then(() => {
        window.location.reload();
      });
  };

  return (
    <div key={headerLabel} className={styles.lesson}>
      <Text asChild size="sm" weight="bold">
        <button
          className={cn(
            'tw-bg-transparent tw-border-none tw-cursor-pointer',
            'hover:tw-bg-white hover:tw-bg-opacity-25 focus:tw-bg-white focus:tw-bg-opacity-25',
            outlineStyles,
            styles.tagButton,
          )}
          type="button"
          onClick={() => toggleExpanded(`Lesson-${lesson.id}`)}
        >
          <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} tw-mr-[8px]`} />
          {headerLabel}
          {isExpanded && isLoading && <i className="fa fa-spinner fa-spin tw-ml-[8px]" />}
        </button>
      </Text>

      {isExpanded && (
        <div className="tw-ml-[20px] tw-mt-[8px]">
          {isLoading && (
            <div>Loading tag values...</div>
          )}

          {!isLoading && !tagValues && (
            <div>No content available for this tag</div>
          )}

          {!isLoading && tagValues && (
            <ul className="tw-list-disc">
              {tagValues.classroomActivities?.map(classroomActivity => (
                <li key={`${lesson.title} ${classroomActivity.id}`}>
                  <Text asChild size="sm">
                    <a
                      className={lesson.comingSoon ? styles.disabledLink : outlineStyles}
                      href={classroomActivity.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {classroomActivity.label}
                    </a>
                  </Text>
                </li>
              ))}
              {tagValues.videoActivities?.map(videoActivity => (
                <li key={`${lesson.title} ${videoActivity.id}`}>
                  <Text asChild size="sm">
                    <a
                      className={lesson.comingSoon ? styles.disabledLink : outlineStyles}
                      href={videoActivity.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {videoActivity.label}
                    </a>
                  </Text>
                </li>
              ))}
              {tagValues.sections?.map(section => (
                <li key={section.id}>
                  <Text asChild size="sm">
                    <a
                      className={lesson.comingSoon ? styles.disabledLink : outlineStyles}
                      href={Routes.shared_section_path(section.id, { program_id: programId })}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {`${I18n.t('reading')} / ${section.label}`}
                    </a>
                  </Text>
                </li>
              ))}
              {tagValues.questions?.map(question => (
                <li key={question.id}>
                  <Text asChild size="sm">
                    <a
                      className={lesson.comingSoon ? styles.disabledLink : outlineStyles}
                      href={question.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {question.label}
                    </a>
                  </Text>
                </li>
              ))}
            </ul>
          )}

          {isSysadmin && (
            <button
              className="btn btn--red btn--sm tw-mt-1"
              disabled={isClearingCache}
              type="button"
              onClick={e => handleClearingCache(e, lesson.id)}
            >
              <i className="fa fa-lock" /> Clear Cache
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LessonTagContent;
